import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  User, 
  Home, 
  FileText, 
  CreditCard, 
  Download,
  Calendar,
  MapPin,
  Phone,
  Mail,
  Building,
  Euro,
  CheckCircle,
  Clock,
  FileDown
} from 'lucide-react';

// Simulazione dati utente per preview
const DEMO_USER = {
  id: "demo-user",
  nome: "Mario",
  cognome: "Rossi",
  codice_fiscale: "RSSMRA80A01H501Z",
  email: "mario.rossi@email.it",
  telefono: "333 1234567",
  indirizzo: "Via Roma 10",
  citta: "Firenze",
  cap: "50100",
  iban: "IT60X0542811101000000123456",
  pec: "mario.rossi@pec.it"
};

const DEMO_IMMOBILE = {
  id: "demo-immobile",
  indirizzo: "Via Baccio da Montelupo 123",
  citta: "Scandicci",
  cap: "50018",
  provincia: "FI",
  tipo: "Appartamento",
  mq: 75,
  vani: 3
};

const DEMO_CONTRATTO = {
  id: "demo-contratto",
  codice: "20240100001",
  data_inizio: "01/01/2024",
  data_fine: "31/12/2027",
  stato: "attivo",
  canone: 650,
  spese_condominiali: 50,
  proprietario: "Bianchi Luigi"
};

const DEMO_PAGAMENTI = [
  { id: 1, mese: "Marzo 2026", importo: 752.50, stato: "pagato", data_pagamento: "05/03/2026", fattura: true },
  { id: 2, mese: "Febbraio 2026", importo: 752.50, stato: "pagato", data_pagamento: "05/02/2026", fattura: true },
  { id: 3, mese: "Gennaio 2026", importo: 752.50, stato: "pagato", data_pagamento: "05/01/2026", fattura: true },
  { id: 4, mese: "Dicembre 2025", importo: 752.50, stato: "pagato", data_pagamento: "05/12/2025", fattura: true },
  { id: 5, mese: "Novembre 2025", importo: 752.50, stato: "pagato", data_pagamento: "05/11/2025", fattura: true },
];

const PortaleUtentePreview = () => {
  const [activeTab, setActiveTab] = useState('anagrafica');
  const user = DEMO_USER;
  const immobile = DEMO_IMMOBILE;
  const contratto = DEMO_CONTRATTO;
  const pagamenti = DEMO_PAGAMENTI;

  const formatCurrency = (value) => {
    return `€ ${value.toLocaleString('it-IT', { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                <Building className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">SEMPLIFICAFFITTO</h1>
                <p className="text-xs text-slate-500">Area Riservata</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-slate-700">{user.nome} {user.cognome}</p>
                <p className="text-xs text-slate-500">Inquilino</p>
              </div>
              <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-slate-600" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Banner Preview */}
      <div className="bg-amber-50 border-b border-amber-200 py-2">
        <div className="max-w-6xl mx-auto px-4">
          <p className="text-sm text-amber-800 text-center font-medium">
            ⚠️ ANTEPRIMA PORTALE UTENTI - Dati dimostrativi
          </p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 w-full max-w-2xl mb-8">
            <TabsTrigger value="anagrafica" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">Anagrafica</span>
            </TabsTrigger>
            <TabsTrigger value="immobili" className="flex items-center gap-2">
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Immobili</span>
            </TabsTrigger>
            <TabsTrigger value="contratti" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Contratti</span>
            </TabsTrigger>
            <TabsTrigger value="pagamenti" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Pagamenti</span>
            </TabsTrigger>
          </TabsList>

          {/* ANAGRAFICA */}
          <TabsContent value="anagrafica">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    Dati Personali
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Nome</label>
                      <p className="font-medium">{user.nome}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Cognome</label>
                      <p className="font-medium">{user.cognome}</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Codice Fiscale</label>
                    <p className="font-mono font-medium">{user.codice_fiscale}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <div>
                        <label className="text-xs text-slate-500 uppercase">Email</label>
                        <p className="text-sm">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <div>
                        <label className="text-xs text-slate-500 uppercase">Telefono</label>
                        <p className="text-sm">{user.telefono}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-blue-600" />
                    Indirizzo e Dati Bancari
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Indirizzo Residenza</label>
                    <p className="font-medium">{user.indirizzo}</p>
                    <p className="text-sm text-slate-600">{user.cap} {user.citta}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase">IBAN</label>
                    <p className="font-mono text-sm bg-slate-100 p-2 rounded">{user.iban}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase">PEC</label>
                    <p className="text-sm">{user.pec}</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileDown className="w-5 h-5 text-blue-600" />
                    Documenti
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <Button variant="outline" className="justify-start h-auto py-3">
                      <Download className="w-4 h-4 mr-2 text-blue-600" />
                      <div className="text-left">
                        <p className="font-medium">Carta d'Identità</p>
                        <p className="text-xs text-slate-500">PDF - 1.2 MB</p>
                      </div>
                    </Button>
                    <Button variant="outline" className="justify-start h-auto py-3">
                      <Download className="w-4 h-4 mr-2 text-blue-600" />
                      <div className="text-left">
                        <p className="font-medium">Codice Fiscale</p>
                        <p className="text-xs text-slate-500">PDF - 0.5 MB</p>
                      </div>
                    </Button>
                    <Button variant="outline" className="justify-start h-auto py-3">
                      <Download className="w-4 h-4 mr-2 text-blue-600" />
                      <div className="text-left">
                        <p className="font-medium">Mandato SEPA</p>
                        <p className="text-xs text-slate-500">PDF - 0.8 MB</p>
                      </div>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* IMMOBILI */}
          <TabsContent value="immobili">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Home className="w-5 h-5 text-emerald-600" />
                  Immobile in Locazione
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="w-full md:w-48 h-32 bg-slate-200 rounded-lg flex items-center justify-center">
                    <Home className="w-12 h-12 text-slate-400" />
                  </div>
                  <div className="flex-1 space-y-4">
                    <div>
                      <h3 className="text-xl font-bold">{immobile.indirizzo}</h3>
                      <p className="text-slate-600">{immobile.cap} {immobile.citta} ({immobile.provincia})</p>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <p className="text-xs text-slate-500 uppercase">Tipo</p>
                        <p className="font-medium">{immobile.tipo}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <p className="text-xs text-slate-500 uppercase">Superficie</p>
                        <p className="font-medium">{immobile.mq} mq</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <p className="text-xs text-slate-500 uppercase">Vani</p>
                        <p className="font-medium">{immobile.vani}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">
                        <Download className="w-4 h-4 mr-2" />
                        Visura Catastale
                      </Button>
                      <Button variant="outline" size="sm">
                        <Download className="w-4 h-4 mr-2" />
                        Planimetria
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CONTRATTI */}
          <TabsContent value="contratti">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  Contratto di Locazione
                </CardTitle>
                <Badge className="bg-emerald-100 text-emerald-700">
                  {contratto.stato.toUpperCase()}
                </Badge>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <label className="text-xs text-slate-500 uppercase">Codice Contratto</label>
                        <p className="font-mono font-bold text-lg">{contratto.codice}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-slate-500 uppercase">Data Inizio</label>
                          <p className="font-medium flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            {contratto.data_inizio}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 uppercase">Data Fine</label>
                          <p className="font-medium flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            {contratto.data_fine}
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 uppercase">Proprietario</label>
                        <p className="font-medium">{contratto.proprietario}</p>
                      </div>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                      <h4 className="font-semibold text-slate-700">Riepilogo Economico Mensile</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Canone</span>
                          <span className="font-medium">{formatCurrency(contratto.canone)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Spese Condominiali</span>
                          <span className="font-medium">{formatCurrency(contratto.spese_condominiali)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Bollo</span>
                          <span className="font-medium">{formatCurrency(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Commissioni SA</span>
                          <span className="font-medium">{formatCurrency(contratto.canone * 0.09)}</span>
                        </div>
                        <hr />
                        <div className="flex justify-between text-lg font-bold">
                          <span>Totale Mensile</span>
                          <span className="text-blue-600">
                            {formatCurrency(contratto.canone + contratto.spese_condominiali + 2 + (contratto.canone * 0.09))}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline">
                      <Download className="w-4 h-4 mr-2" />
                      Contratto Firmato (PDF)
                    </Button>
                    <Button variant="outline">
                      <Download className="w-4 h-4 mr-2" />
                      Mandato di Servizio
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PAGAMENTI */}
          <TabsContent value="pagamenti">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                  Cronistoria Pagamenti
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pagamenti.map((pag) => (
                    <div 
                      key={pag.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          pag.stato === 'pagato' ? 'bg-emerald-100' : 'bg-amber-100'
                        }`}>
                          {pag.stato === 'pagato' ? (
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <Clock className="w-5 h-5 text-amber-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{pag.mese}</p>
                          <p className="text-sm text-slate-500">
                            {pag.stato === 'pagato' ? `Pagato il ${pag.data_pagamento}` : 'In attesa'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-lg">{formatCurrency(pag.importo)}</p>
                          <Badge variant={pag.stato === 'pagato' ? 'default' : 'secondary'} className={
                            pag.stato === 'pagato' ? 'bg-emerald-100 text-emerald-700' : ''
                          }>
                            {pag.stato === 'pagato' ? 'Pagato' : 'Da pagare'}
                          </Badge>
                        </div>
                        {pag.fattura && (
                          <Button variant="ghost" size="sm" className="text-blue-600">
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Prossimo Addebito</p>
                      <p className="text-2xl font-bold text-blue-700">Aprile 2026</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-blue-600">Importo previsto</p>
                      <p className="text-2xl font-bold text-blue-700">{formatCurrency(752.50)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-4 mt-8">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-slate-500">
          © 2026 Semplificaffitto.it - Area Riservata Utenti
        </div>
      </footer>
    </div>
  );
};

export default PortaleUtentePreview;
